"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import Navbar from "../components/Navbar"
import { createTransaction, getProductById } from "../utils/api"
import type { Product } from "../types"
import { useAuth } from "../context/AuthContext"
import "./ProductDetails.css"
import api from "../utils/api"

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentImage, setCurrentImage] = useState(0)
  const { user } = useAuth()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [purchaseLoading, setPurchaseLoading] = useState(false)

  const handleInitiatePurchase = async () => {
    try {
      setPurchaseLoading(true)
      if (product?.id) {
        await createTransaction(product.id)
        setShowSuccessModal(true)
      }
    } catch (err) {
      alert("Error starting transaction.")
    } finally {
      setPurchaseLoading(false)
    }
  }

  const handleContactSeller = async () => {
    try {
      const response = await api.post(
        "/chat/conversations",
        { recipient_id: product?.seller_id },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      )
      window.location.href = `/chat`
    } catch (err) {
      console.error("Failed to start conversation", err)
    }
  }

  const formatDate = (dateString: string | undefined) => {
    if(dateString){
      const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    }
  }

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await getProductById(Number(id))
        setProduct(response.data)
      } catch (err) {
        console.error("Failed to load product", err)
      } finally {
        setLoading(false)
      }
    }
    if (id) fetchProduct()
  }, [id])

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="product-details-container-productdetails">
          <div className="container">
            <div className="loading-state-productdetails">
              <div className="loading-spinner-productdetails"></div>
              <p>Loading product details...</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <div className="product-details-container-productdetails">
          <div className="container">
            <div className="error-state-productdetails">
              <div className="error-icon-productdetails">❌</div>
              <h3>Product Not Found</h3>
              <p>The product you're looking for doesn't exist or has been removed.</p>
              <Link to="/" className="action-btn-productdetails primary-btn-productdetails">
                <span className="btn-icon">🏠</span>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Navbar />
      <div className="product-details-container-productdetails">
        <div className="container">
          <div className="breadcrumb-productdetails">
            <Link to="/" className="breadcrumb-link-productdetails">
              Products
            </Link>
            <span className="breadcrumb-separator-productdetails">›</span>
            <span className="breadcrumb-current-productdetails">{product.name}</span>
          </div>

          <div className="product-details-card-productdetails">
            <div className="product-gallery-productdetails">
              <div className="main-image-container-productdetails">
                {product.images.length > 0 ? (
                  <>
                    <img
                      src={product.images[currentImage]?.image_url || "/placeholder.svg"}
                      alt={`${product.name} - Image ${currentImage + 1}`}
                      className="main-image-productdetails"
                    />
                    {product.images.length > 1 && (
                      <>
                        <button
                          className="carousel-arrow-productdetails left"
                          onClick={() => setCurrentImage((prev) => (prev === 0 ? product.images.length - 1 : prev - 1))}
                          aria-label="Previous image"
                        >
                          ‹
                        </button>
                        <button
                          className="carousel-arrow-productdetails right"
                          onClick={() => setCurrentImage((prev) => (prev === product.images.length - 1 ? 0 : prev + 1))}
                          aria-label="Next image"
                        >
                          ›
                        </button>
                        <div className="image-counter-productdetails">
                          {currentImage + 1} / {product.images.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="no-image-placeholder-productdetails">
                    <div className="placeholder-icon-productdetails">📷</div>
                    <p>No image available</p>
                  </div>
                )}
              </div>

              {product.images.length > 1 && (
                <div className="thumbnail-container-productdetails">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      className={`thumbnail-productdetails ${index === currentImage ? "active" : ""}`}
                      onClick={() => setCurrentImage(index)}
                    >
                      <img src={image.image_url || "/placeholder.svg"} alt={`Thumbnail ${index + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="product-info-productdetails">
              <div className="product-header-productdetails">
                <div className="product-status-productdetails">
                </div>
                <h1 className="product-title-productdetails">{product.name}</h1>
                <div className="product-price-productdetails">RM{product.price}</div>
              </div>

              <div className="product-meta-productdetails">
                <div className="meta-section-productdetails">
                  <h3 className="meta-title-productdetails">Product Information</h3>
                  <div className="meta-grid-productdetails">
                    <div className="meta-item-productdetails">
                      <span className="meta-icon-productdetails">👤</span>
                      <div className="meta-content-productdetails">
                        <span className="meta-label-productdetails">Seller</span>
                        <span className="meta-value-productdetails">{product.seller_username}</span>
                      </div>
                    </div>
                    <div className="meta-item-productdetails">
                      <span className="meta-icon-productdetails">📍</span>
                      <div className="meta-content-productdetails">
                        <span className="meta-label-productdetails">Pickup Location</span>
                        <span className="meta-value-productdetails">{product.pickup_location}</span>
                      </div>
                    </div>
                    <div className="meta-item-productdetails">
                      <span className="meta-icon-productdetails">📅</span>
                      <div className="meta-content-productdetails">
                        <span className="meta-label-productdetails">Posted</span>
                        <span className="meta-value-productdetails">{formatDate(product.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="description-section-productdetails">
                  <h3 className="section-title-productdetails">Description</h3>
                  <div className="description-content-productdetails">
                    <p>{product.description}</p>
                  </div>
                </div>
              </div>

              <div className="product-actions-productdetails">
                {!product.is_sold && (
                  <button
                    onClick={handleInitiatePurchase}
                    disabled={purchaseLoading || user?.id === product.seller_id}
                    className="action-btn-productdetails primary-btn-productdetails buy-btn"
                  >
                    <span className="btn-icon">🛒</span>
                    {purchaseLoading ? "Processing..." : "Buy Now"}
                  </button>
                )}

                {user && user.id !== product.seller_id ? (
                  <button onClick={handleContactSeller} className="action-btn-productdetails secondary-btn-productdetails">
                    <span className="btn-icon">💬</span>
                    Chat with Seller
                  </button>
                ) : user && user.id === product.seller_id ? (
                  <Link to={`/manage-product/${product.id}`} className="action-btn-productdetails accent-btn-productdetails">
                    <span className="btn-icon">⚙️</span>
                    Manage Product
                  </Link>
                ) : (
                  <Link to="/login" className="action-btn-productdetails outline-btn-productdetails">
                    <span className="btn-icon">🔐</span>
                    Login to Contact
                  </Link>
                )}

                <Link to="/" className="action-btn-productdetails outline-btn-productdetails">
                  <span className="btn-icon">←</span>
                  Back to Products
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSuccessModal && (
        <div className="modal-overlay-productdetails">
          <div className="success-modal-productdetails">
            <div className="success-icon-productdetails">✓</div>
            <h3>Transaction Started!</h3>
            <p>Your purchase request has been sent to the seller. You can now chat with them to confirm the details.</p>
            <div className="modal-actions-productdetails">
              <button onClick={() => setShowSuccessModal(false)} className="action-btn-productdetails primary-btn-productdetails">
                Continue Shopping
              </button>
              <Link to="/chat" className="action-btn-productdetails secondary-btn-productdetails">
                Go to Chat
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ProductDetails
